
    import Component from "vue-class-component";
    import Vue from "vue";
    import {
        MedicalPractitionerModel, MedicalPractitionerViewModel, SysAdminMedicalPractitionersClient
    } from "@/api-client";
    import { BvTableFieldArray } from "bootstrap-vue";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import HeadingWave from "@/features/shared/components/HeadingWave.vue";
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import { Prop } from "vue-property-decorator";
    import {stringOrEmptyPlaceholder} from "@/extensions/string.helpers";
    import {translateTrueFalse} from "@/features/shared/TrueFalseTranslator";
    import {NavigationGuardNext} from "vue-router/types/router";
    import {debounceFn} from "debounce-decorator-ts";

    interface Props {
        preloadedMedicalPractitioners: MedicalPractitionerViewModel[];
    }

    @Component({
        components: {
            TableEmptySlot,
            HeadingWave,
            SectionPageHeading
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<MedicalPractitioners>) {
            const props = to.meta!.props as Props;
            Promise.all([
                new SysAdminMedicalPractitionersClient().list()
            ]).then(values => {
                props.preloadedMedicalPractitioners = values[0];
                next();
            }).catch(e => next(e))
        }
    })
    export default class MedicalPractitioners extends Vue implements Props {
        @Prop({ required: true })
        preloadedMedicalPractitioners!: MedicalPractitionerViewModel[];

        medicalPractitioners: MedicalPractitionerViewModel[] = this.preloadedMedicalPractitioners;
        filteredMedicalPractitioners = this.medicalPractitioners.map(x => {
            return {
                id: x.medicalPractitionerModels[0].id,
                firstName: x.medicalPractitionerModels[0].firstName,
                lastName: x.medicalPractitionerModels[0].lastName,
                organizationId: x.medicalPractitionerModels[0].organizationId,
                organizationName: x.medicalPractitionerModels.map(function (elem) {
                    return elem.organizationName;
                }),
                isActive: x.medicalPractitionerModels[0].isActive,
                representative: x.medicalPractitionerModels.map(function (elem) {
                    return elem.representative?.fullName;
                })
            };
        });

        searchPhrase = "";
        initialSort = { sortBy: "fullName", sortDesc: false };

        created() {
            this.getParametersFromRouteOrDefault();

            this.$watch(() => this.searchPhrase, () => {
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.$route.query, () => {
                this.search();
            }, { deep: true, immediate: true });
        }

        search(){
            this.filteredMedicalPractitioners = this.medicalPractitioners.filter(o => `${o.medicalPractitionerModels[0].firstName} ${o.medicalPractitionerModels[0].lastName}`.toLowerCase().indexOf(this.searchPhrase.toLowerCase()) >= 0).map(x => {
                return {
                    id: x.medicalPractitionerModels[0].id,
                    firstName: x.medicalPractitionerModels[0].firstName,
                    lastName: x.medicalPractitionerModels[0].lastName,
                    organizationId: x.medicalPractitionerModels[0].organizationId,
                    organizationName: x.medicalPractitionerModels.map(function (elem) {
                        return elem.organizationName;
                    }),
                    isActive: x.medicalPractitionerModels[0].isActive,
                    representative: x.medicalPractitionerModels.map(function (elem) {
                        return elem.representative?.fullName;
                    })
                };
            });
        }

        editMedicalPractitioner(update: MedicalPractitionerModel) {
            this.$router.push({ name: 'edit-medical-practitioner', params: { id: update.id! } });
        }

        @debounceFn(100) // Prevents possible double-update
        reflectParametersToRoute(): void {
            this.$router.replace({
                query: {
                    // Setting to 'undefined' removes parameter from URL.
                    searchPhrase: this.searchPhrase || undefined
                }
            });
        }

        getParametersFromRouteOrDefault() {
            const route = this.$route as any;
            const query = route.query;
            this.searchPhrase = query.searchPhrase ? query.searchPhrase : '';
        }

        fields: BvTableFieldArray = [
            {
                key: "fullName",
                label: this.$ls.Global_Label_FullName,
                sortable: true,
                sortByFormatted: true,
                formatter: (value: any, key: any, item: MedicalPractitionerModel): string => `${item.firstName} ${item.lastName}`
            },
            {
                key: "organizationName",
                label: this.$ls.Administration_MedicalPractitioners_Table_Label_Organization,
                sortable: true,
            },
            {
                key: "representativeFullName",
                label: this.$ls.Administration_MedicalPractitioners_Table_Label_LinkedRepresentative,
                sortable: true,
                formatter: (value: any, key: any, item: MedicalPractitionerModel): string => stringOrEmptyPlaceholder(item.representative?.fullName)
            },
            {
                key: "isActive",
                label: this.$ls.Global_Label_Active,
                sortable: true,
                sortByFormatted: true,
                formatter: (value: any, key: any, item: MedicalPractitionerModel): string => translateTrueFalse(item.isActive)
            },
            {
                key: "linkIcon",
                label: "",
                sortable: false,
                tdClass:"table-actions"
            },
        ];
    }
